import React, {FC, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Button} from 'reactstrap';
import {TnToastActions} from '@touchnet/react-toast';
import TnToggleSwitch from '@touchnet/react-toggle-switch';
import confirm from '@touchnet/react-confirm-modal';
import {TnReactTable, useDatatable} from '@touchnet/react-table';
import {
    createColumnHelper,
    getCoreRowModel
} from '@tanstack/react-table';

import {withRouter} from '~/providers/with-router.tsx';
import withCurrentWorktags from '../../providers/with-common-worktags';
import withWorktags from '../../providers/with-worktags';

import {CommonWorktag} from '~/types/common-worktag-type';

import CustomCreatableSelect from '../general/custom-creatable-select';
import CommonWorktagActionButton from './common-worktag-action-button';
import {NavigateFunction} from 'react-router-dom';
import {actions} from "~/state/reducers/worktag-reducer.ts";

const REQUIRED = 'required';
const CAN_EDIT = 'canEdit';

interface ComponentProps {
    commonWorktagsDirty: boolean;
    setCommonWorktagsDirty: (dirty: boolean) => void;
    worktags: string[];
    currentCommonWorktags: CommonWorktag[];
    navigate: NavigateFunction;
}

export const CommonWorktagTable: FC<ComponentProps> = ({
    commonWorktagsDirty,
    setCommonWorktagsDirty,
    worktags,
    currentCommonWorktags,
    navigate
}: ComponentProps) => {
    const dispatch = useDispatch();
    const [saveAttempted, setSaveAttempted] = useState(false);
    const worktagsEndRef: React.MutableRefObject<HTMLDivElement> = useRef();
    const getWorktagOptions = (worktags: string[]) => worktags.map(worktag => ({label: worktag, value: worktag}));

    const addRow = () => {
        setCommonWorktagsDirty(true);
        dispatch(actions.addNewCommonWorktag());
        worktagsEndRef.current.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
    };

    const attemptSave = () => {
        const namesValid = currentCommonWorktags.reduce((valid, worktag) => (valid ? worktag.code && worktag.code.length > 0 : valid), true);

        if (namesValid) {
            confirm({
                cancelLabel: 'Never mind',
                children: 'The worktag definitions have been updated. If a required definition was added, existing accounting codes will need to be updated to populate a value. Worktags to be deleted will also be removed from all existing accounting codes. Do you wish to proceed?',
                okLabel: 'Yes, go ahead',
                title: 'Save Changes?',
            }).then(
                () => {
                    dispatch(actions.saveCommonWorktags(navigate));
                    setCommonWorktagsDirty(false);
                    setSaveAttempted(false);
                },
                () => {
                    setCommonWorktagsDirty(true);
                    setSaveAttempted(true);
                }
            );
        }
    };

    const columns = useMemo(() => {
      const columnHelper = createColumnHelper<CommonWorktag>();

      const getWorktagSelect = (row: CommonWorktag) => {
        if (row.required) {
          return <span>{row.code}</span>;
        }

        const selectProps = {
          isValid: Boolean(row.code) && row.code.length > 0,
          validBeforeTouched: saveAttempted,
          isMulti: false,
          closeMenuOnSelect: true,
          editable: true,
          isSearchable: true,
          onChange: (opt: { value: string }) => onSelectWorktag(opt.value, row),
          options: getWorktagOptions(worktags),
          value: { value: row.code, label: row.code },
          classes: "align-middle",
          placeholder: "Enter or select worktag",
          invalidMessage: "A worktag name is required",
        };

        return <CustomCreatableSelect {...selectProps} />;
      };

      const onSelectWorktag = (worktagName: string, row: CommonWorktag) => {
        setCommonWorktagsDirty(true);
        if (worktagExists(worktagName, row)) {
            dispatch(TnToastActions.showErrorMessage("Duplicate Worktags not Allowed"));
        } else {
            dispatch(actions.setCommonWorktag({ key: row.key, code: worktagName }));
        }
      };

      const worktagExists = (worktagName: string, row: CommonWorktag) => {
        for (const commonWorktag of currentCommonWorktags) {
          if (
            commonWorktag.key !== row.key &&
            commonWorktag.code === worktagName
          ) {
            return true;
          }
        }

        return false;
      };

      const getToggle = (
        toggleValue: boolean,
        worktag: CommonWorktag,
        field: string
      ) => {
        return (
          <TnToggleSwitch
            id={`${field}-${worktag.key}`}
            className="align-middle"
            checked={
              toggleValue === true || (field === REQUIRED && worktag.required)
            }
            onChange={(checked) => toggleStatus(checked, worktag.key, field)}
          />
        );
      };

      const toggleStatus = (checked: boolean, key: number, field: string) => {
        setCommonWorktagsDirty(true);
        if (field === REQUIRED) {
            dispatch(actions.setRequiredForCommonWorktag({ key: key, required: checked }));
        } else if (field === CAN_EDIT) {
            dispatch(actions.setCanEditForCommonWorktag({ key: key, editable: checked }));
        }
      };

      return [
        columnHelper.accessor("code", {
          header: "Worktag",
          size: 50,
          cell: (cellContext) => getWorktagSelect(cellContext.row.original),
        }),
        columnHelper.accessor("required", {
          header: "Required",
          size: 10,
          cell: (cellContext) =>
            getToggle(
              cellContext.getValue(),
              cellContext.row.original,
              REQUIRED
            ),
        }),
        columnHelper.accessor("editable", {
          header: "Can Edit",
          size: 10,
          cell: (cellContext) =>
            getToggle(
              cellContext.getValue(),
              cellContext.row.original,
              CAN_EDIT
            ),
        }),
        columnHelper.display({
          id: "actions",
          header: "Actions",
          size: 30,
          cell: ({ row }) => (
            <CommonWorktagActionButton
              {...{
                worktag: row.original,
                setCommonWorktagsDirty: setCommonWorktagsDirty,
              }}
            />
          ),
        }),
      ];
    }, [
      currentCommonWorktags,
      saveAttempted,
      setCommonWorktagsDirty,
      worktags,
    ]);

    const table = useDatatable({
        columns: columns,
        data: currentCommonWorktags,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <div className="text-right mb-2">
                <span className="d-inline-block mr-1">
                    <Button color="secondary" outline onClick={() => addRow()}>Add Row</Button>
                </span>
                <span className="d-inline-block">
                    <Button color="primary" disabled={!commonWorktagsDirty} onClick={() => attemptSave()}>Save</Button>
                </span>
            </div>
            <div className="react-bootstrap-table">
                <TnReactTable table={table} />
            </div>
            <div ref={worktagsEndRef} />
        </>
    );
}

export default withRouter(
  withCurrentWorktags(
    withWorktags(
      CommonWorktagTable
    )
  )
);

